import React, { useState, useEffect } from 'react';
import { usersApi, passwordResetApi, api } from '../lib/api';
import { formatDate } from '../lib/utils';
import { User, Plus, Trash2, Key, Search, UserCheck, ExternalLink, X, Building2, AlertTriangle, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [pendingResets, setPendingResets] = useState([]);

  // Form state
  const [userType, setUserType] = useState('employee');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeResults, setEmployeeResults] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searching, setSearching] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'teller', office_id: '' });
  const [offices, setOffices] = useState([]);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [usrRes, offRes, resetsRes] = await Promise.all([
        usersApi.list(),
        api.get('/offices').catch(() => ({ data: [] })),
        passwordResetApi.listRequests().catch(() => ({ data: [] })),
      ]);
      setUsers(usrRes.data);
      setOffices(offRes.data || []);
      setPendingResets(resetsRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleApproveReset = async (req) => {
    if (!req.user_exists) { toast.error('No staff account matches this email.'); return; }
    if (!window.confirm(`Reset password for ${req.user_name || req.email}?\n\nA temporary password will be generated, emailed to them (if email is configured), and they'll be forced to change it on next login.`)) return;
    try {
      const res = await passwordResetApi.adminReset(req.user_id, req.id);
      setResetResult(res.data);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Reset failed'); }
  };

  const handleDismissReset = async (req) => {
    if (!window.confirm(`Dismiss reset request from ${req.email}?`)) return;
    try {
      await passwordResetApi.dismissRequest(req.id);
      toast.success('Request dismissed');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Dismiss failed'); }
  };

  const searchEmployees = async (q) => {
    setEmployeeSearch(q);
    if (q.length < 2) { setEmployeeResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/employees/search?q=${encodeURIComponent(q)}`);
      setEmployeeResults(res.data || []);
    } catch { setEmployeeResults([]); }
    setSearching(false);
  };

  const selectEmployee = (emp) => {
    setSelectedEmployee(emp);
    setForm(f => ({
      ...f,
      name: `${emp.first_name} ${emp.last_name}`.trim(),
      email: emp.email || '',
      office_id: emp.office_id || '',
    }));
    setEmployeeResults([]);
    setEmployeeSearch('');
  };

  const handleSubmit = async () => {
    if (!form.email || !form.password || !form.name) return toast.error('Name, email, and password are required');
    const payload = {
      ...form,
      employee_id: selectedEmployee?.id || null,
      user_type: userType,
    };
    try {
      await usersApi.create(payload);
      toast.success(`User created${selectedEmployee ? ` and linked to employee: ${selectedEmployee.first_name} ${selectedEmployee.last_name}` : ''}`);
      resetForm();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create user'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user? This removes system access but does not delete the employee record.')) return;
    try { await usersApi.delete(id); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('Reset this user\'s password?')) return;
    try {
      const res = await passwordResetApi.adminReset(userId);
      setResetResult(res.data);
      toast.success('Password reset successfully');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reset password'); }
  };

  const resetForm = () => {
    setShowModal(false);
    setUserType('employee');
    setSelectedEmployee(null);
    setEmployeeSearch('');
    setEmployeeResults([]);
    setForm({ email: '', password: '', name: '', role: 'teller', office_id: '' });
  };

  return (
    <div className="space-y-6 fade-in" data-testid="users-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Admin</span><span>/</span><span className="text-[#1f1f1f] font-medium">Users</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">USER MANAGEMENT</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Users</h1>
          <p className="text-xs text-gray-500 mt-1">System access accounts. Link to employees for staff, or create standalone for external auditors.</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" /> Create User
        </Button>
      </div>

      {/* Pending Password Reset Requests — shown only when there are any */}
      {pendingResets.length > 0 && (
        <div className="bg-amber-50 border border-amber-300" data-testid="pending-resets-panel">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-200">
            <AlertTriangle className="w-4 h-4 text-amber-700" />
            <h3 className="font-semibold text-amber-900 text-sm">Pending Password Reset Requests ({pendingResets.length})</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-amber-900/70 bg-amber-100/40">
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Matched user</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">Requested</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingResets.map(r => (
                <tr key={r.id} className="border-t border-amber-200/60" data-testid={`pending-reset-row-${r.id}`}>
                  <td className="px-4 py-2.5 text-amber-950">{r.email}</td>
                  <td className="px-4 py-2.5">
                    {r.user_exists ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800">
                        <Check className="w-3 h-3" />{r.user_name}
                      </span>
                    ) : (
                      <span className="text-[11px] text-red-700 font-semibold">No matching user</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs capitalize">{(r.user_role || '—').replace(/_/g, ' ')}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-600">{r.requested_at ? formatDate(r.requested_at) : '—'}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <Button size="sm" onClick={() => handleApproveReset(r)}
                            disabled={!r.user_exists}
                            className="bg-[#1f1f1f] hover:bg-[#333333] text-white mr-1"
                            data-testid={`approve-reset-${r.id}`}>
                      <Key className="w-3.5 h-3.5 mr-1" />Reset & Email
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDismissReset(r)}
                            className="text-gray-600" data-testid={`dismiss-reset-${r.id}`}>
                      <X className="w-3.5 h-3.5 mr-1" />Dismiss
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-gray-200">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Name</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Email</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Role</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Created</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            users.map(u => (
              <tr key={u.id} className="border-b hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 flex items-center justify-center text-white text-xs ${u.employee_id ? 'bg-[#1f1f1f]' : 'bg-gray-400'}`}>
                      {u.employee_id ? <UserCheck className="w-3.5 h-3.5" /> : <ExternalLink className="w-3.5 h-3.5" />}
                    </div>
                    <span className="font-medium text-sm text-[#1f1f1f]">{u.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-1 bg-[#1f1f1f]/10 text-[#1f1f1f] capitalize">{u.role?.replace(/_/g, ' ')}</span></td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 ${u.employee_id ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{u.employee_id ? 'Staff' : u.user_type === 'external' ? 'External' : 'Staff'}</span></td>
                <td className="px-4 py-3 text-sm text-gray-600">{u.created_at ? formatDate(u.created_at) : '-'}</td>
                <td className="px-4 py-3 flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleResetPassword(u.id)} className="text-[#C5A059]" title="Reset Password" data-testid={`reset-pwd-${u.id}`}><Key className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id)} className="text-red-500" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      <Dialog open={showModal} onOpenChange={v => { if (!v) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby="user-form-desc">
          <DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
          <p id="user-form-desc" className="sr-only">Create system user account</p>
          <div className="space-y-4">
            {/* User Type Selection */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">User Type</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => { setUserType('employee'); setSelectedEmployee(null); setForm(f => ({...f, name: '', email: '', office_id: ''})); }}
                  className={`p-3 border text-left transition-colors ${userType === 'employee' ? 'border-[#C5A059] bg-amber-50' : 'border-gray-200 hover:bg-gray-50'}`}
                  data-testid="type-employee"
                >
                  <UserCheck className={`w-5 h-5 mb-1 ${userType === 'employee' ? 'text-[#C5A059]' : 'text-gray-400'}`} />
                  <p className="text-sm font-medium text-[#1f1f1f]">Employee</p>
                  <p className="text-[10px] text-gray-500">Link to existing employee record</p>
                </button>
                <button
                  onClick={() => { setUserType('external'); setSelectedEmployee(null); setForm(f => ({...f, name: '', email: '', office_id: ''})); }}
                  className={`p-3 border text-left transition-colors ${userType === 'external' ? 'border-[#C5A059] bg-amber-50' : 'border-gray-200 hover:bg-gray-50'}`}
                  data-testid="type-external"
                >
                  <ExternalLink className={`w-5 h-5 mb-1 ${userType === 'external' ? 'text-[#C5A059]' : 'text-gray-400'}`} />
                  <p className="text-sm font-medium text-[#1f1f1f]">External</p>
                  <p className="text-[10px] text-gray-500">Auditors, consultants, regulators</p>
                </button>
              </div>
            </div>

            {/* Employee Search (only when type=employee) */}
            {userType === 'employee' && !selectedEmployee && (
              <div>
                <Label>Search Employee</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                  <Input
                    className="pl-9"
                    value={employeeSearch}
                    onChange={e => searchEmployees(e.target.value)}
                    placeholder="Search by name or email..."
                    data-testid="employee-search"
                  />
                </div>
                {searching && <p className="text-xs text-gray-400 mt-1">Searching...</p>}
                {employeeResults.length > 0 && (
                  <div className="border border-gray-200 mt-1 max-h-40 overflow-y-auto">
                    {employeeResults.map(emp => (
                      <button
                        key={emp.id}
                        onClick={() => selectEmployee(emp)}
                        disabled={emp.has_system_access}
                        className={`w-full text-left px-3 py-2 text-sm border-b border-gray-50 ${emp.has_system_access ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'hover:bg-blue-50 cursor-pointer'}`}
                        data-testid={`emp-result-${emp.id}`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="font-medium">{emp.first_name} {emp.last_name}</span>
                            <span className="text-xs text-gray-400 ml-2">{emp.position}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {emp.employee_number && <span className="text-[10px] font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5">{emp.employee_number}</span>}
                            {emp.has_system_access && <span className="text-[10px] bg-gray-200 text-gray-500 px-1.5 py-0.5">Already linked</span>}
                          </div>
                        </div>
                        {emp.email && <p className="text-xs text-gray-500">{emp.email}</p>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Selected Employee Badge */}
            {selectedEmployee && (
              <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200" data-testid="selected-employee">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-emerald-600" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">{selectedEmployee.first_name} {selectedEmployee.last_name}</p>
                    <p className="text-xs text-emerald-600">{selectedEmployee.employee_number ? `${selectedEmployee.employee_number} - ` : ''}{selectedEmployee.position} {selectedEmployee.email ? `- ${selectedEmployee.email}` : ''}</p>
                  </div>
                </div>
                <button onClick={() => { setSelectedEmployee(null); setForm(f => ({...f, name: '', email: '', office_id: ''})); }} className="text-emerald-400 hover:text-emerald-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* User details */}
            <div><Label>Full Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="user-name" readOnly={!!selectedEmployee} className={selectedEmployee ? 'bg-gray-50' : ''} /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="user-email" /></div>
            <div><Label>Password *</Label><Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} data-testid="user-password" placeholder="Temporary password" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Role *</Label>
                <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="loan_officer">Loan Officer</SelectItem>
                    <SelectItem value="teller">Teller</SelectItem>
                    <SelectItem value="accountant">Accountant</SelectItem>
                    <SelectItem value="auditor">Auditor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Branch</Label>
                <Select value={form.office_id || 'none'} onValueChange={v => setForm({...form, office_id: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="user-office"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Branch</SelectItem>
                    {offices.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {userType === 'external' && (
              <p className="text-xs text-gray-400 p-2 bg-gray-50 border">External users are not linked to employee records. Suitable for auditors, SARB inspectors, compliance consultants, and other non-staff who need system access.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="user-submit">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Result Modal */}
      <Dialog open={!!resetResult} onOpenChange={() => setResetResult(null)}>
        <DialogContent aria-describedby="reset-result-desc">
          <DialogHeader><DialogTitle>Password Reset Complete</DialogTitle></DialogHeader>
          <p id="reset-result-desc" className="sr-only">Temporary password details</p>
          {resetResult && (
            <div className="space-y-4" data-testid="reset-result">
              <div className="p-4 bg-emerald-50 border border-emerald-200">
                <p className="text-sm text-emerald-800 font-medium">Password has been reset for {resetResult.user_name || resetResult.user_email}</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Temporary Password</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 p-3 bg-gray-100 border border-gray-200 text-lg font-mono text-[#1f1f1f] select-all" data-testid="temp-password">{resetResult.temporary_password}</code>
                  <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(resetResult.temporary_password); toast.success('Copied!'); }} data-testid="copy-password">Copy</Button>
                </div>
              </div>
              <p className="text-xs text-gray-500">Share this temporary password securely. User should change it on next login.</p>

              {/* Email delivery status */}
              {resetResult.email?.sent ? (
                <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Temporary password emailed to <b>{resetResult.user_email}</b>.
                </div>
              ) : resetResult.email_enabled ? (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2">
                  ⚠ Email could not be sent: {resetResult.email?.reason || 'unknown error'}.
                  Please share the temporary password with the user manually.
                </div>
              ) : (
                <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 px-3 py-2">
                  📧 Email sending is not yet configured. Share the temporary password with the user manually (phone/WhatsApp).
                  Admins can activate email via <b>Admin → System → External Services → add Resend</b>.
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button onClick={() => setResetResult(null)} className="bg-[#1f1f1f] text-white">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
